import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";

import { MfaService, VerifyMfaCodeResult, CompleteMfaVerificationResult } from "@ha/feature/mfa";
import { SignInCommand } from "../../auth/models/sign-in-command";
import { SignIn } from "../../auth/states/sign-in.actions";
import { SettingsStateActions } from "../../core/states/settings.actions";
import { SettingsState } from "../../core/states/settings.state";

@Component({
    selector: "app-login-mfa-verification",
    templateUrl: "./login-mfa-verification.component.html",
    styleUrls: ["./login-mfa-verification.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@UntilDestroy()
export class LoginMfaVerificationComponent implements OnInit {
    private fb = inject(FormBuilder);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private mfaService = inject(MfaService);
    private cdr = inject(ChangeDetectorRef);
    private store = inject(Store);

    loading = true;
    saving = false;
    error = "";

    // Store original sign-in credentials for continuation after MFA verification
    originalSignInCommand?: SignInCommand;
    userId?: string;

  @Select(SettingsState.currentLanguage)
    public currentLanguage$: Observable<string>;

  @Select(SettingsState.uiLanguages)
  public uiLanguages$: Observable<string[]>;

  form = this.fb.group({
      verificationCode: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]]
  });

  ngOnInit(): void {
      this.loading = true;

      // Retrieve stored sign-in credentials from session storage
      const storedCredentials = sessionStorage.getItem("mfa-verification-credentials");
      if (storedCredentials) {
          this.originalSignInCommand = JSON.parse(storedCredentials);
      }

      // Get userId from query parameters
      this.userId = this.route.snapshot.queryParams["Id"];

      if (!this.userId) {
          console.error("No userId provided for MFA verification");
          this.error = "errors.INVALID_VERIFICATION_REQUEST";
          this.loading = false;
          this.cdr.detectChanges();
          return;
      }

      this.loading = false;
      this.cdr.detectChanges();
  }

  verify(): void {
      if (this.form.invalid) {
          this.form.markAllAsTouched();
          return;
      }

      this.saving = true;
      this.error = "";
      const code = this.form.value.verificationCode!;

      if (!this.userId) {
          this.error = "errors.INVALID_VERIFICATION_REQUEST";
          this.saving = false;
          this.cdr.detectChanges();
          return;
      }

      // Step 1: Verify MFA code
      this.mfaService.verifyMfaCode(this.userId, code).pipe(
          untilDestroyed(this)
      ).subscribe({
          next: (result) => {
              if (result.success && result.autoLoginRequired) {
                  // Step 2: Complete MFA verification and auto-login
                  this.completeMfaVerificationAndLogin();
              } else {
                  const code = result.errorCode;
                  this.error = code ? `errors.${code}` : "errors.UNKNOWN";
                  this.saving = false;
                  this.cdr.detectChanges();
              }
          },
          error: (err) => {
              console.error("MFA verification error:", err);
              const code = err?.error?.errorCode;
              this.error = code ? `errors.${code}` : "errors.UNKNOWN";
              this.saving = false;
              this.cdr.detectChanges();
          }
      });
  }

  private completeMfaVerificationAndLogin(): void {
      // Step 2: Complete MFA verification and auto-login
      this.mfaService.completeMfaVerification(this.userId).pipe(
          untilDestroyed(this)
      ).subscribe({
          next: (result) => {
              if (result.status === 1) {
                  // Success! User is automatically logged in

                  // Reset saving state before redirect
                  this.saving = false;
                  this.cdr.detectChanges();

                  // Clear stored credentials
                  sessionStorage.removeItem("mfa-verification-credentials");

                  // Redirect to intended destination or home
                  const returnUrl = this.originalSignInCommand?.returnUrl || "/";
                  window.location.href = returnUrl;
              } else {
                  const code = (result as any)?.errorCode;
                  this.error = code ? `errors.${code}` : "errors.UPDATE_FAILED";
                  this.saving = false;
                  this.cdr.detectChanges();
              }
          },
          error: (err) => {
              console.error("Complete MFA verification error:", err);
              const code = err?.error?.errorCode;
              this.error = code ? `errors.${code}` : "errors.UPDATE_FAILED";
              this.saving = false;
              this.cdr.detectChanges();
          }
      });
  }

  languageChanged(languageCode: string): void {
      this.store.dispatch(new SettingsStateActions.ChangeLanguage(languageCode));
  }
}
